import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Icon } from "@/components/iconMap";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { EstimateButton, CallButton, TextButton } from "@/components/CTAButtons";
import { EstimateCTASection, SectionHeading } from "@/components/Sections";
import { services, pricing, business } from "@/data/site";

export default function Services() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Junk Removal & Hauling",
    provider: { "@type": "LocalBusiness", name: business.name },
    areaServed: business.serviceArea,
    description: "Full-service junk removal including furniture, appliances, garage, basement, attic, estate and property cleanouts.",
  };
  const preview = pricing.slice(0, 4);

  return (
    <>
      <Seo
        title="Junk Removal Services | Benji's Hauling & Junk Removal"
        description="Furniture removal, appliance removal, garage & basement cleanouts, estate & property cleanouts, yard debris and more. Full-service loading, hauling, and cleanup included."
        path="/services"
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: "Services" }]} />
      <PageHero
        eyebrow="Services"
        title="Full-Service Junk Removal & Hauling"
        sub="We handle the lifting, loading, hauling, and cleanup, so you don't have to. Here's what Benji's team can take off your hands."
      >
        <div className="flex flex-wrap gap-3">
          <EstimateButton label="Request a Free Estimate" variant="primary" />
          <CallButton variant="ghostLight" />
        </div>
      </PageHero>

      {/* Detailed service list */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <StaggerGroup className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <StaggerItem key={s.slug} id={s.slug} className="group flex h-full flex-col rounded-lg border border-border bg-white p-6 transition-all hover:-translate-y-1 hover:border-gold/50 hover:shadow-md">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-forest/5 text-forest transition-colors group-hover:bg-gold/15 group-hover:text-gold-dark">
                  <Icon name={s.icon} className="h-6 w-6" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-forest">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{s.blurb}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal className="mt-10 flex flex-col items-center gap-4 rounded-lg border border-border bg-accent/50 p-8 text-center">
            <Camera className="h-7 w-7 text-gold-dark" />
            <p className="font-heading text-lg font-bold text-forest">Don't see your item listed? Send us a photo and ask!</p>
            <div className="flex flex-wrap justify-center gap-3">
              <TextButton label="Text Us a Photo" />
              <EstimateButton label="Request a Free Estimate" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="bg-accent/40 py-20">
        <div className="container-x">
          <Reveal>
            <SectionHeading eyebrow="Transparent pricing" title="Simple, Volume-Based Pricing" align="center" sub={`Pricing is primarily based on how much space your items take up in our truck. Every load includes labor, loading, hauling, and cleanup. Starting at $${business.startingPrice}.`} />
          </Reveal>
          <StaggerGroup className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {preview.map((p) => (
              <StaggerItem key={p.size} className="rounded-lg border border-border bg-white p-5 text-center">
                <p className="text-xs font-bold uppercase tracking-wide text-mutedgreen">{p.size}</p>
                <p className="mt-2 font-heading text-3xl font-extrabold text-forest">${p.price}</p>
                <p className="mt-1 text-xs text-mutedgreen">{p.volume}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal className="mt-8 text-center">
            <Link to="/pricing" data-testid="services-view-pricing" className="inline-flex items-center gap-2 rounded-md bg-forest px-6 py-3.5 text-sm font-bold text-cream transition-all hover:-translate-y-0.5 hover:bg-forest-light">
              View Full Pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <EstimateCTASection />
    </>
  );
}
